import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { StatCard, Section } from "@/pages/Shell";
import { api, formatError } from "@/lib/api";
import { DashboardActionZone } from "@/components/ActionCenter";
import {
  ChartBar, Buildings, WarningOctagon, CheckCircle, CaretRight,
  ListChecks, Clock, TrendUp, Target,
} from "@phosphor-icons/react";

const ACCENT = "#9C5236";
const SOLD_ACCENT = "#C1432A";

const PHASES = ["foundation", "structure", "finishes", "delivery"];
const PHASE_LABEL = {
  foundation: "Foundation",
  structure: "Structure",
  finishes: "Finishes",
  delivery: "Delivery",
};

const STATUS_LABEL = { available: "Available", reserved: "Reserved", sold: "Sold" };
const STATUS_STYLE = {
  sold: "bg-red-50 text-red-700 border-red-200",
  reserved: "bg-amber-50 text-amber-700 border-amber-200",
  available: "bg-stone-50 text-stone-600 border-stone-200",
};

const PRIORITY_ORDER = { sold: 0, reserved: 1, available: 2 };
const PRIORITY_LABEL = { sold: "HIGH", reserved: "MEDIUM", available: "LOW" };
const PRIORITY_STYLE = {
  sold: "bg-red-600 text-white",
  reserved: "bg-amber-500 text-white",
  available: "bg-stone-300 text-stone-700",
};

function nextPhaseOf(currentPhase, percentage) {
  if (!currentPhase) return "foundation";
  const idx = PHASES.indexOf(currentPhase);
  if (idx === -1) return "foundation";
  if (currentPhase === "delivery" && percentage >= 100) return null; // delivered
  if (percentage < 100) return currentPhase; // still current
  return PHASES[idx + 1] || null;
}

function daysSince(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d)) return null;
  return Math.floor((Date.now() - d.getTime()) / 86400000);
}

export default function ConstructorHub({ onNavigate }) {
  const navigate = useNavigate();
  const [villas, setVillas] = useState([]);
  const [progress, setProgress] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [error, setError] = useState("");

  const load = async () => {
    try {
      const [v, p, t] = await Promise.all([
        api.get("/villas"), api.get("/progress"), api.get("/tasks"),
      ]);
      setVillas(v.data); setProgress(p.data); setTasks(t.data);
    } catch (e) { setError(formatError(e)); }
  };
  useEffect(() => { load(); }, []);

  // Per-villa latest progress
  const villaData = useMemo(() => {
    const byVilla = {};
    for (const pr of progress) {
      const cur = byVilla[pr.villa_id];
      if (!cur || new Date(pr.created_at || 0) > new Date(cur.created_at || 0)) {
        byVilla[pr.villa_id] = pr;
      }
    }
    return villas.map(v => {
      const latest = byVilla[v.villa_id];
      const phase = latest?.phase || null;
      const pct = Number(latest?.percentage) || 0;
      const next = nextPhaseOf(phase, pct);
      const blockers = tasks.filter(t => t.is_blocker && t.villa_id === v.villa_id && t.status !== "done");
      return {
        ...v,
        latest_phase: phase,
        latest_pct: pct,
        latest_at: latest?.created_at || null,
        next_milestone: next,
        blockers_count: blockers.length,
        priority: v.status, // sold/reserved/available
      };
    }).sort((a, b) => {
      const pa = PRIORITY_ORDER[a.status] ?? 9;
      const pb = PRIORITY_ORDER[b.status] ?? 9;
      if (pa !== pb) return pa - pb;
      return (b.latest_pct || 0) - (a.latest_pct || 0);
    });
  }, [villas, progress, tasks]);

  const blockerTasks = useMemo(
    () => tasks.filter(t => t.is_blocker && t.status !== "done"),
    [tasks]
  );
  const pendingTasks = useMemo(
    () => tasks
      .filter(t => t.status !== "done")
      .sort((a, b) => {
        const pr = { alta: 0, media: 1, baja: 2 };
        return (pr[a.priority] ?? 1) - (pr[b.priority] ?? 1);
      }),
    [tasks]
  );

  // Active = villas that are sold/reserved OR have at least one progress row.
  // Used for the top StatCards and the Villas overview table.
  const active = villaData.filter(v => v.status !== "available" || v.latest_phase);

  const soldPendingDelivery = villaData.filter(v => v.status === "sold" && v.latest_pct < 100).length;
  const totalProgress = active.length
    ? Math.round(active.reduce((s, v) => s + v.latest_pct, 0) / active.length)
    : 0;

  const toggleTaskDone = async (t) => {
    await api.patch(`/tasks/${t.task_id}`, { status: t.status === "done" ? "todo" : "done" });
    load();
  };

  return (
    <div>
      <DashboardActionZone lang="en" onViewAll={() => onNavigate?.("action-center")} />

      <Section title="Construction operations" subtitle="Real-time view of milestones, blockers and pending actions.">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard label="Active villas" value={active.length} sub={`of ${villas.length} total`} accent={ACCENT} testid="stat-active-villas" />
          <StatCard label="Sold pending delivery" value={soldPendingDelivery} sub="highest priority" accent={SOLD_ACCENT} testid="stat-sold-pending" />
          <StatCard label="Project progress" value={`${totalProgress}%`} sub="weighted avg" accent={ACCENT} testid="stat-project-progress" />
          <StatCard label="Open blockers" value={blockerTasks.length} sub={blockerTasks.length === 0 ? "none" : "action needed"} accent={blockerTasks.length > 0 ? SOLD_ACCENT : "#2F3E35"} testid="stat-blockers" />
        </div>
      </Section>

      {error && <div className="dv-card text-red-700 mb-4">{error}</div>}

      {/* Blockers */}
      <Section title={`Blockers (${blockerTasks.length})`} subtitle="Issues preventing progress. Resolve or reassign.">
        <div className="dv-card" data-testid="blockers-panel">
          {blockerTasks.length === 0 ? (
            <div className="flex items-center gap-3 text-stone-500 py-4">
              <CheckCircle size={24} weight="fill" className="text-emerald-500" />
              <span>No active blockers. Nice work.</span>
            </div>
          ) : (
            <div className="space-y-2">
              {blockerTasks.map(t => {
                const villa = villas.find(v => v.villa_id === t.villa_id);
                return (
                  <div key={t.task_id} className="flex items-start gap-3 p-3 rounded-md border border-red-100 bg-red-50/40" data-testid={`blocker-${t.task_id}`}>
                    <WarningOctagon size={18} weight="fill" className="text-red-600 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium">{t.title}</div>
                      {t.description && <div className="text-xs text-stone-500 mt-0.5">{t.description}</div>}
                      <div className="text-xs text-stone-400 mt-1">
                        {villa ? <>Villa: <span className="font-medium">{villa.name}</span></> : "Project-wide"}
                        {t.priority && <> · Priority: <span className="uppercase">{t.priority}</span></>}
                      </div>
                    </div>
                    <button onClick={() => toggleTaskDone(t)} className="dv-btn dv-btn-ghost text-xs" data-testid={`blocker-resolve-${t.task_id}`}>
                      Mark resolved
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </Section>

      {/* Pending actions */}
      <Section title={`Pending actions (${pendingTasks.length})`} subtitle="Open tasks sorted by priority.">
        <div className="dv-card p-0 overflow-hidden" data-testid="pending-panel">
          {pendingTasks.length === 0 ? (
            <div className="text-center text-stone-400 py-8">No pending actions.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-stone-50 border-b border-stone-200 text-left">
                <tr className="text-xs tracking-wider uppercase text-stone-500">
                  <th className="px-4 py-3">Task</th>
                  <th className="px-4 py-3">Villa</th>
                  <th className="px-4 py-3">Priority</th>
                  <th className="px-4 py-3">Category</th>
                  <th className="px-4 py-3 text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {pendingTasks.slice(0, 10).map(t => {
                  const villa = villas.find(v => v.villa_id === t.villa_id);
                  const prio = t.priority || "media";
                  const prioStyle = prio === "alta" ? "text-red-700" : prio === "media" ? "text-amber-700" : "text-emerald-700";
                  const prioLbl = prio === "alta" ? "HIGH" : prio === "media" ? "MEDIUM" : "LOW";
                  return (
                    <tr key={t.task_id} className="border-b border-stone-100" data-testid={`pending-${t.task_id}`}>
                      <td className="px-4 py-3">
                        <div className="font-medium">{t.title}</div>
                        {t.is_blocker && <span className="dv-badge dv-badge-alta text-[10px]">BLOCKER</span>}
                      </td>
                      <td className="px-4 py-3 text-stone-500">{villa?.name || "—"}</td>
                      <td className={`px-4 py-3 font-medium ${prioStyle}`}>{prioLbl}</td>
                      <td className="px-4 py-3 text-stone-500">{t.category || "general"}</td>
                      <td className="px-4 py-3 text-right">
                        <button onClick={() => toggleTaskDone(t)} className="text-xs text-stone-600 hover:text-stone-900" data-testid={`pending-done-${t.task_id}`}>
                          Mark done
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </Section>

      {/* Villas table */}
      <Section title="Villas overview" subtitle="Sold villas are prioritised. Click a villa to open its 360 view.">
        <div className="dv-card p-0 overflow-hidden" data-testid="villas-table">
          <table className="w-full text-sm">
            <thead className="bg-stone-50 border-b border-stone-200 text-left">
              <tr className="text-xs tracking-wider uppercase text-stone-500">
                <th className="px-4 py-3">Villa</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Current phase</th>
                <th className="px-4 py-3">Progress</th>
                <th className="px-4 py-3">Next milestone</th>
                <th className="px-4 py-3 text-center">Blockers</th>
                <th className="px-4 py-3">Priority</th>
                <th className="px-4 py-3">Last update</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {villaData.map(v => {
                const isSold = v.status === "sold";
                const days = daysSince(v.latest_at);
                return (
                  <tr
                    key={v.villa_id}
                    className="border-b border-stone-100 hover:bg-stone-50 cursor-pointer transition-colors"
                    onClick={() => navigate(`/villa/${v.villa_id}`)}
                    data-testid={`villa-row-${v.villa_id}`}
                    style={isSold ? { borderLeft: `3px solid ${SOLD_ACCENT}` } : {}}
                  >
                    <td className="px-4 py-3">
                      <div className="font-medium">{v.name}</div>
                      <div className="text-xs text-stone-400">{v.model}</div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-[11px] px-2 py-0.5 rounded border uppercase tracking-wider ${STATUS_STYLE[v.status]}`}>
                        {STATUS_LABEL[v.status] || v.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-stone-600">
                      {v.latest_phase ? PHASE_LABEL[v.latest_phase] : <span className="text-stone-300">—</span>}
                    </td>
                    <td className="px-4 py-3 w-32">
                      <div className="h-1.5 bg-stone-100 rounded overflow-hidden">
                        <div className="h-full" style={{ width: `${v.latest_pct}%`, background: isSold ? SOLD_ACCENT : ACCENT }} />
                      </div>
                      <div className="text-xs text-stone-500 mt-1">{v.latest_pct}%</div>
                    </td>
                    <td className="px-4 py-3 text-stone-600">
                      {v.next_milestone ? PHASE_LABEL[v.next_milestone] : (
                        <span className="text-emerald-600 inline-flex items-center gap-1"><CheckCircle size={12} weight="fill" /> Delivered</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {v.blockers_count > 0 ? (
                        <span className="text-red-600 font-medium inline-flex items-center gap-1" data-testid={`villa-blockers-${v.villa_id}`}>
                          <WarningOctagon size={12} weight="fill" /> {v.blockers_count}
                        </span>
                      ) : (
                        <span className="text-stone-300">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium tracking-wider ${PRIORITY_STYLE[v.status]}`}>
                        {PRIORITY_LABEL[v.status]}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-stone-500">
                      {days === null ? "No updates" : days === 0 ? "Today" : `${days}d ago`}
                    </td>
                    <td className="px-4 py-3 text-stone-300"><CaretRight size={14} /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Section>
    </div>
  );
}
